import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { lineString as turfLineString } from '@turf/turf';
import { Feature, GeoJSON, Geometry, Position } from 'geojson';
import { GeoJSONSource, LayerSpecification, MapMouseEvent } from 'maplibre-gl';
import { firstValueFrom } from 'rxjs';
import { v4 } from 'uuid';
// @ts-expect-error No type declaration type exists
import { stringify as wktStringify } from 'wkt';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { MapBaseSource } from '@connect-services/client/components/map/map.component';
import { NavigationStateService } from '@connect-services/client/guards/reload.guard';
import { RouteNameEnum, RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    DepositsService,
    DepositWithFolderAndStandard,
    EditAction,
    Feature as SdkFeature,
    FeatureCollection,
    GeometryService,
    InformationSchemaTableExtended,
    Layer,
    LayerAttribute,
    LayersService,
    LineString,
    MapboxStyle,
    MultiLineString,
    MultiPoint,
    MultiPolygon,
    Point,
    Polygon,
} from '@connect-services/client/sdk/api';
import { EditEntityDetailService } from '@connect-services/client/services/edit-entity-detail.service';
import { EntityDetailCreationContextService } from '@connect-services/client/services/entity-detail-creation-context.service';
import { LocationService } from '@connect-services/client/services/location.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { MapEditGeomService } from '@connect-services/client/services/map-edit-geom.service';
import { MapEditModeService, TARGET_CURSOR } from '@connect-services/client/services/map-edit-mode.service';
import { MapModal, MapModalService } from '@connect-services/client/services/map-modal.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export enum ActiveGeometryTypeEnum {
    FILL = 'fill',
    LINE = 'line',
    SYMBOL = 'symbol',
}

type LayerType = LayerSpecification['type'];

const TYPE_MAP: Partial<Record<MapboxStyle.TypeEnum, LayerType>> = {
    [MapboxStyle.TypeEnum.SYMBOL]: 'symbol',
    [MapboxStyle.TypeEnum.FILL]: 'fill',
    [MapboxStyle.TypeEnum.LINE]: 'line',
    [MapboxStyle.TypeEnum.BACKGROUND]: 'background',
    [MapboxStyle.TypeEnum.CIRCLE]: 'circle',
    [MapboxStyle.TypeEnum.FILL_EXTRUSION]: 'fill-extrusion',
    [MapboxStyle.TypeEnum.HEATMAP]: 'heatmap',
    [MapboxStyle.TypeEnum.HILLSHADE]: 'hillshade',
    [MapboxStyle.TypeEnum.RASTER]: 'raster',
};

type MapCreationContext = {
    deposit: DepositWithFolderAndStandard;
    entityId: string;
    layer: Layer;
};

@Injectable({
    providedIn: 'root',
})
export class MapCreationGeomService extends MapBaseService {
    private static readonly MAP_CREATION_PHANTOM_LAYER_ID = 'map-creation-phantom-layer';
    private static readonly MAP_CREATION_PHANTOM_SOURCE_ID = 'map-creation-phantom-source';

    public activeLayers: Layer[] = [];
    public readonly activeLayerStyle = signal<MapboxStyle | null>(null);
    public attributeInformationList: InformationSchemaTableExtended[] = [];
    public readonly canLeaveCreationPanel = signal<boolean>(false);
    public readonly currentEntityGeometryLength = signal<number>(0);
    public currentGeometryPosition: Position[] = [];
    public readonly currentLayerGeometryType = signal<
        | Point.TypeEnum
        | MultiPoint.TypeEnum
        | LineString.TypeEnum
        | MultiLineString.TypeEnum
        | Polygon.TypeEnum
        | MultiPolygon.TypeEnum
    >(Point.TypeEnum.POINT);
    public readonly editedLayerIds = signal<Set<string>>(new Set());
    public readonly entityId = signal<string>('');
    public layerAttributes: LayerAttribute[] = [];
    public readonly selectedDeposit = signal<DepositWithFolderAndStandard | null>(null);
    public readonly selectedLayer = computed<Layer | null>(() => {
        this.selectedDeposit();

        return (
            this.activeLayers.find(
                (layer) => !!this.selectedDeposit()?.id && this.selectedDeposit()?.id === layer.deposit,
            ) ?? null
        );
    });
    public readonly sourceAttributes = signal<Record<string, unknown> | null>(null);
    public readonly sourceLayerId = signal<string | null>(null);
    public readonly sourceTableName = signal<string | null>(null);

    private createdEntityIds: string[] = [];
    private readonly depositsService = inject(DepositsService);
    private drawCreationGeomPhantomPolygonFunctionInstance?: (mapMouseEvent: MapMouseEvent) => void;
    private readonly editEntityDetailService = inject(EditEntityDetailService);
    private readonly entityDetailCreationContextService = inject(EntityDetailCreationContextService);
    private finalGeometryPosition: Position[] = [];
    private readonly geometryService = inject(GeometryService);
    private readonly isEditionPanelOpen = signal<boolean>(false);
    private readonly layersService = inject(LayersService);
    private readonly locationService = inject(LocationService);
    private readonly mapEditGeomService = inject(MapEditGeomService);
    private readonly mapEditModeService = inject(MapEditModeService);
    private readonly mapModalService = inject(MapModalService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly navigationService = inject(NavigationStateService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);
    private readonly snackBarService = inject(SnackBarService);
    private readonly sourcesService = inject(SourcesService);

    public constructor() {
        super();

        effect(() => {
            this.mapEditModeService.activeGeometryType();
            if (!this.isMapPresent()) {
                return;
            }

            if (!this.mapEditModeService.activeGeometryType()) {
                this.map.getCanvas().style.cursor = 'grab';
                this.mapModalService.close(MapModal.SNAP);

                return;
            }

            this.map.getCanvas().style.cursor = TARGET_CURSOR;
            this.currentGeometryPosition = [];
            this.currentEntityGeometryLength.set(0);
            this.finalGeometryPosition = [];
            this.mapEditGeomService.nextPoints = [];
            this.mapEditGeomService.previousPoints = [];
        });

        effect(async () => {
            if (this.mapEditModeService.isMapEditModeActive()) {
                return;
            }

            await untracked(async () => {
                await this.leaveCreationMode();
            });
        });

        effect(async () => {
            if (
                !this.mapEditModeService.selectedSnapSources().length ||
                !this.mapEditGeomService.snapTolerance() ||
                !this.mapEditModeService.activeGeometryType()
            ) {
                this.mapEditGeomService.mapOffSnapVertexHighlightEvent();
                this.mapEditGeomService.resetSnapStyle();

                return;
            }

            await this.mapSourceService.applySnapStyle(this.mapEditModeService.selectedSnapSources());
            this.mapEditGeomService.mapOnSnapVertexHighlightEvent();
        });

        effect(() => {
            const isAddModeActive = this.mapEditModeService.isAddEntityModeActive();
            const isGeographicMode = this.mapEditModeService.isGeographicEditModeActive();

            if (isAddModeActive || isGeographicMode) {
                return;
            }

            this.resetPhantomPolygonStyle();
            this.resetGeometry();
        });

        effect(() => {
            this.mapEditModeService.activeEditLayerIds();

            this.layerAttributes = [];
            this.attributeInformationList = [];
        });
    }

    public async closeCreationPanel(): Promise<void> {
        if (this.map) {
            this.map.getCanvas().style.cursor = TARGET_CURSOR;
        }

        if (!this.router.url.split('/').some((urlParams: string) => this.createdEntityIds.includes(urlParams))) {
            return;
        }

        const projectId = this.projectBloc.value()?.id;
        if (!projectId) {
            return;
        }

        await this.locationService.navigate([RoutePathEnum.PROJECTS, projectId]);
    }

    public async createEntity(mapClickEvent: MapMouseEvent): Promise<void> {
        if (!this.currentGeometryPosition.length && !this.editEntityDetailService.recomputeError()) {
            await this.recomputeEntityProperties();
        }

        if (this.editEntityDetailService.recomputeError()) {
            this.snackBarService.openErrorSnackBar('editMode.recomputeErrorCorrectError');

            return;
        }

        switch (this.mapEditModeService.activeGeometryType()) {
            case ActiveGeometryTypeEnum.SYMBOL:
                this.createShape(mapClickEvent);
                break;
            case ActiveGeometryTypeEnum.LINE:
                this.createDraftLine(mapClickEvent);
                break;
            default:
                this.createDraftPolygon(mapClickEvent);
        }
        this.canLeaveCreationPanel.set(true);
    }

    public async createShape(mapClickEvent?: MapMouseEvent): Promise<void> {
        if (this.editEntityDetailService.recomputeError()) {
            this.snackBarService.openErrorSnackBar('editMode.recomputeErrorCorrectError');

            return;
        }

        const projectId = this.projectBloc.value()?.id;
        if (!this.canCreateShape() || !projectId) {
            return;
        }

        this.prepareCreation();

        this.finalGeometryPosition = this.currentGeometryPosition;

        if (mapClickEvent) {
            this.finalGeometryPosition.push(
                this.mapEditGeomService.selectedSnapPoint()?.position ?? [
                    mapClickEvent.lngLat.lng,
                    mapClickEvent.lngLat.lat,
                ],
            );
        }

        const context = await this.resolveMapContext();
        if (!context) {
            return;
        }
        this.editEntityDetailService.resetFormsValues();
        this.initCreationContext(context);
        this.openCreationPanel();

        await this.navigateToEntityDetail(projectId, context.entityId, context.layer.id);
        this.currentGeometryPosition = [];
        untracked(() => this.currentEntityGeometryLength.set(0));
        this.canLeaveCreationPanel.set(false);
    }

    public async getAdditionType(): Promise<void> {
        if (!this.map || !this.mapEditModeService.isMapEditModeActive()) {
            return;
        }

        const additionSources = this.sourcesService
            .sources()
            .filter((source: MapBaseSource<unknown>) =>
                this.mapEditModeService.activeEditLayerIds().has(source.sourceId),
            );

        if (!additionSources.length) {
            return;
        }

        try {
            const layerId = additionSources[0].sourceId;
            const layer = await firstValueFrom(this.layersService.layerControllerGetOne({ id: layerId }));

            if (!layer.geometryType) {
                return;
            }

            const sourceStyles = additionSources[0].style;
            const creationMode = this.transformLayerGeometryTypeToCreationMode(layer.geometryType);

            const activeStyle = sourceStyles.find((style: MapboxStyle) => style.type === creationMode);

            this.activeLayerStyle.set(activeStyle ?? null);

            this.currentLayerGeometryType.set(layer.geometryType);
            this.mapEditModeService.activeGeometryType.set(this.getCurrentLayerGeometryType());
        } catch {
            /* empty */
        }
    }

    public getCreatedGeom(): Geometry | null {
        let geom: Geometry | null = null;

        if (!this.finalGeometryPosition.length) {
            return null;
        }

        switch (this.currentLayerGeometryType()) {
            case Point.TypeEnum.POINT:
                geom = {
                    coordinates: this.finalGeometryPosition[0],
                    type: Point.TypeEnum.POINT,
                };
                break;
            case MultiPoint.TypeEnum.MULTI_POINT:
                geom = {
                    coordinates: [this.finalGeometryPosition[0]],
                    type: MultiPoint.TypeEnum.MULTI_POINT,
                };
                break;
            case LineString.TypeEnum.LINE_STRING:
                geom = {
                    coordinates: this.finalGeometryPosition,
                    type: LineString.TypeEnum.LINE_STRING,
                };
                break;
            case MultiLineString.TypeEnum.MULTI_LINE_STRING:
                geom = {
                    coordinates: [this.finalGeometryPosition],
                    type: MultiLineString.TypeEnum.MULTI_LINE_STRING,
                };
                break;
            case Polygon.TypeEnum.POLYGON:
                geom = {
                    coordinates: [[...this.finalGeometryPosition, this.finalGeometryPosition[0]]],
                    type: Polygon.TypeEnum.POLYGON,
                };
                break;
            case MultiPolygon.TypeEnum.MULTI_POLYGON:
                geom = {
                    coordinates: [[[...this.finalGeometryPosition, this.finalGeometryPosition[0]]]],
                    type: MultiPolygon.TypeEnum.MULTI_POLYGON,
                };
                break;
        }

        return geom;
    }

    public isCurrentLayerGeometryLine(): boolean {
        return (
            this.currentLayerGeometryType() === LineString.TypeEnum.LINE_STRING ||
            this.currentLayerGeometryType() === MultiLineString.TypeEnum.MULTI_LINE_STRING
        );
    }

    public isCurrentLayerGeometryPoint(): boolean {
        return (
            this.currentLayerGeometryType() === Point.TypeEnum.POINT ||
            this.currentLayerGeometryType() === MultiPoint.TypeEnum.MULTI_POINT
        );
    }

    public isCurrentLayerGeometryPolygon(): boolean {
        return (
            this.currentLayerGeometryType() === Polygon.TypeEnum.POLYGON ||
            this.currentLayerGeometryType() === MultiPolygon.TypeEnum.MULTI_POLYGON
        );
    }

    public async leaveCreationMode(): Promise<void> {
        this.mapEditModeService.clearLayerEditMode();
        this.mapModalService.close(MapModal.SNAP);

        this.resetCreationData();
        await this.closeCreationPanel();
        this.stopCreationMode();
    }

    public openTabularCreationPanel(): void {
        this.navigationService.markNavigated();
        this.isEditionPanelOpen.set(true);
    }

    public async recomputeEntityProperties(): Promise<boolean> {
        const selectedDeposit = this.selectedDeposit();
        if (!selectedDeposit?.standard) {
            return Promise.resolve(true);
        }

        const geom: Geometry | null =
            (this.editEntityDetailService.after.get('geom') as Geometry | null) ?? this.getCreatedGeom();

        const success = await this.editEntityDetailService.recomputeEntityProperties(
            geom,
            EditAction.CREATE,
            null,
            true,
        );

        if (!success) {
            this.snackBarService.openErrorSnackBar('editMode.recomputeErrorCorrectError');
        }

        return success;
    }

    public redrawCustomLayer(): void {
        this.resetCreateLayer();
        this.setLayerStyle();
        this.createCustomLayer();
    }

    public resetCreationData(): void {
        this.resetGeometry();
        this.resetMapCreationState();
    }

    public resetCreationEntityRelatedData(): void {
        this.editEntityDetailService.resetRecomputeError();
        this.canLeaveCreationPanel.set(false);
        this.selectedDeposit.set(null);
        this.sourceAttributes.set(null);
        this.sourceLayerId.set(null);
        this.sourceTableName.set(null);
        this.activeLayers = [];
    }

    public resetGeometry(): void {
        this.mapEditGeomService.nextPoints = [];
        this.mapEditGeomService.previousPoints = [];
        this.resetCreateLayer();
    }

    public resetMapCreationState(): void {
        this.entityId.set('');
        this.isEditionPanelOpen.set(false);

        this.editedLayerIds.set(new Set([...this.editedLayerIds(), ...this.mapEditModeService.activeEditLayerIds()]));
    }

    public setCreationEntityRelatedData(
        deposit: DepositWithFolderAndStandard,
        sourceLayerId: string,
        sourceTableName: string,
        targetLayer: Layer,
        entityId: string,
        attributes: Record<string, unknown>,
    ): void {
        this.activeLayers = [targetLayer];
        this.selectedDeposit.set(deposit);
        this.entityId.set(entityId);
        this.sourceAttributes.set(attributes);
        this.sourceLayerId.set(sourceLayerId);
        this.sourceTableName.set(sourceTableName);
    }

    public stopCreationMode(): void {
        if (!this.isMapPresent()) {
            return;
        }

        this.map.getCanvas().style.cursor = 'grab';
        this.mapEditModeService.activeGeometryType.set(null);
    }

    public updateCursorAfterCreation(keepAddEntityMode: boolean): void {
        if (!this.map) {
            return;
        }

        this.map.getCanvas().style.cursor = keepAddEntityMode ? TARGET_CURSOR : 'grab';
    }

    private canCreateShape(): boolean {
        if (!this.projectBloc.value() || !this.isMapPresent()) {
            return false;
        }

        if (!this.validateShape()) {
            this.snackBarService.openErrorSnackBar('editMode.formNotValid');

            return false;
        }

        return true;
    }

    private canDrawLine(): boolean {
        return (
            this.currentGeometryPosition.length < 2 ||
            !this.activeLayerStyle() ||
            !this.mapEditModeService.activeGeometryType()
        );
    }

    private createCustomLayer(): void {
        const geometry = this.getCreatedGeom();
        if (!geometry) {
            throw new Error('Geometry must not be null');
        }

        const type = TYPE_MAP[this.activeLayerStyle()!.type];
        if (!type) {
            throw new Error('Type must not be null');
        }

        const feature: Feature = { geometry, properties: {}, type: SdkFeature.TypeEnum.FEATURE };
        this.mapSourceService.addCustomSource(MapEditModeService.PREVIEW_GEOMETRY_SOURCE, [feature]);

        const layerSpecification: LayerSpecification = {
            id: MapEditModeService.PREVIEW_GEOMETRY_LAYER,
            layout: this.activeLayerStyle()!.layout,
            paint: this.activeLayerStyle()!.paint,
            source: MapEditModeService.PREVIEW_GEOMETRY_SOURCE,
            type,
        };
        this.mapSourceService.addCustomLayer(layerSpecification);
    }

    private async createDraftLine(mapClickEvent: MapMouseEvent): Promise<void> {
        if (!this.map) {
            return;
        }

        const newPoint = this.getCurrentPosition(mapClickEvent);
        const toValidateLine: LineString = {
            coordinates: [...this.currentGeometryPosition, newPoint],
            type: LineString.TypeEnum.LINE_STRING,
        };
        const isGeometryValid = await this.validateGeometry(toValidateLine);

        if (!isGeometryValid) {
            return;
        }

        this.handlePhantomLineCreation(newPoint);
        if (this.canDrawLine()) {
            return;
        }

        this.drawCreatedLine();
    }

    private async createDraftPolygon(mapClickEvent: MapMouseEvent): Promise<void> {
        if (!this.map) {
            return;
        }

        const newPoint = this.getCurrentPosition(mapClickEvent);
        const toValidatePolygon: Polygon = {
            coordinates: [[...this.currentGeometryPosition, newPoint, this.currentGeometryPosition[0]]],
            type: Polygon.TypeEnum.POLYGON,
        };
        const isGeometryValid = await this.validateGeometry(toValidatePolygon);

        if (!isGeometryValid) {
            return;
        }

        this.handlePhantomLineCreation(newPoint);
        this.mapEditGeomService.nextPoints = [this.currentGeometryPosition[0]];

        if (this.canDrawLine()) {
            return;
        }

        this.initCreationPolygonGeometry();

        this.drawCreatedLine();
    }

    private drawCreatedLine(): void {
        const lineString = turfLineString(this.currentGeometryPosition);

        this.mapSourceService.addCreationLayer(lineString);
    }

    private async drawCreationGeomPhantomPolygon(mapMouseEvent: MapMouseEvent): Promise<void> {
        const phantomSource = this.getCreationPhantomSource();

        if (!phantomSource) {
            return;
        }

        const phantomDataSource = (await phantomSource.getData()) as FeatureCollection;

        phantomDataSource.features = [this.getPhantomPolygonFeature(mapMouseEvent)];
        phantomSource.setData(phantomDataSource as GeoJSON);
        this.mapSourceService.addPhantomPolygonLayer(
            phantomSource,
            MapCreationGeomService.MAP_CREATION_PHANTOM_SOURCE_ID,
            MapCreationGeomService.MAP_CREATION_PHANTOM_LAYER_ID,
            getComputedStyle(document.documentElement).getPropertyValue('--custom-blue').trim(),
        );
    }

    private finalizeCurrentCreationBeforeStartingNewOne(): void {
        this.entityDetailCreationContextService.finalizeCurrentCreationNow();
        this.resetCreationData();
        this.entityDetailCreationContextService.reset();
    }

    private getCreationPhantomSource(): GeoJSONSource | null {
        if (!this.map || !this.hasAccessibleMapStyle()) {
            return null;
        }

        let phantomSource = this.map?.getSource(MapCreationGeomService.MAP_CREATION_PHANTOM_SOURCE_ID) as GeoJSONSource;

        if (!phantomSource) {
            this.mapSourceService.addCustomSource(MapCreationGeomService.MAP_CREATION_PHANTOM_SOURCE_ID);
            phantomSource = this.map?.getSource(MapCreationGeomService.MAP_CREATION_PHANTOM_SOURCE_ID) as GeoJSONSource;
        }

        return phantomSource;
    }

    private getCurrentLayerGeometryType(): ActiveGeometryTypeEnum {
        if (this.isCurrentLayerGeometryPoint()) {
            return ActiveGeometryTypeEnum.SYMBOL;
        }

        if (this.isCurrentLayerGeometryLine()) {
            return ActiveGeometryTypeEnum.LINE;
        }

        return ActiveGeometryTypeEnum.FILL;
    }

    private getCurrentPosition(mapMouseEvent: MapMouseEvent): Position {
        if (!this.mapEditGeomService.selectedSnapPoint()) {
            return [mapMouseEvent.lngLat.lng, mapMouseEvent.lngLat.lat];
        }

        return this.mapEditGeomService.selectedSnapPoint()!.position;
    }

    private getPhantomPolygonFeature(mapMouseEvent: MapMouseEvent): SdkFeature {
        const coordinates: number[][] = [
            ...this.currentGeometryPosition,
            this.getCurrentPosition(mapMouseEvent),
            this.currentGeometryPosition[0],
        ];

        return {
            geometry: {
                coordinates: [coordinates],
                type: Polygon.TypeEnum.POLYGON,
            },
            properties: {},
            type: SdkFeature.TypeEnum.FEATURE,
        };
    }

    private handlePhantomLineCreation(newPoint: Position): void {
        this.currentGeometryPosition.push(newPoint);
        untracked(() => this.currentEntityGeometryLength.set(this.currentGeometryPosition.length));
        this.mapEditGeomService.previousPoints = [newPoint];
        this.mapEditGeomService.initEditGeometry();
    }

    private hasAccessibleMapStyle(): boolean {
        if (!this.isMapPresent()) {
            return false;
        }

        return !!this.map.style;
    }

    private initCreationContext(context: MapCreationContext): void {
        this.activeLayers = [context.layer];
        this.selectedDeposit.set(context.deposit);

        this.entityDetailCreationContextService.initFromMap({
            depositIds: [context.deposit.id],
            entityId: context.entityId,
            layerIds: [context.layer.id],
        });
    }

    private initCreationPolygonGeometry(): void {
        if (!this.map) {
            return;
        }

        this.map.getCanvas().style.cursor = TARGET_CURSOR;

        if (!this.drawCreationGeomPhantomPolygonFunctionInstance) {
            this.drawCreationGeomPhantomPolygonFunctionInstance = this.drawCreationGeomPhantomPolygon.bind(this);
        }

        this.map.on('mousemove', this.drawCreationGeomPhantomPolygonFunctionInstance);
    }

    private async navigateToEntityDetail(projectId: string, entityId: string, layerId: string): Promise<void> {
        await this.router.navigate([
            RoutePathEnum.PROJECTS,
            projectId,
            RoutePathEnum.LAYERS,
            layerId,
            RouteNameEnum.ENTITY_DETAIL,
            entityId,
            'CREATE',
        ]);
    }

    private openCreationPanel(): void {
        if (!this.map) {
            return;
        }

        this.createCustomLayer();
        this.navigationService.markNavigated();

        this.map.getCanvas().style.cursor = TARGET_CURSOR;
        this.isEditionPanelOpen.set(true);
    }

    private prepareCreation(): void {
        this.finalizeCurrentCreationBeforeStartingNewOne();
        this.mapEditGeomService.resetAddLineVertex();
        this.resetPhantomPolygonStyle();
        this.resetCreateLayer();
    }

    private resetCreateLayer(): void {
        if (!this.map || !this.hasAccessibleMapStyle()) {
            return;
        }

        const layerIds = [MapEditModeService.CREATE_GEOMETRY_LAYER, MapEditModeService.PREVIEW_GEOMETRY_LAYER];
        const sourceIds = [MapEditModeService.CREATE_GEOMETRY_SOURCE, MapEditModeService.PREVIEW_GEOMETRY_SOURCE];

        layerIds.forEach((layerId: string) => {
            if (!this.map?.getLayer(layerId)) {
                return;
            }

            this.map.removeLayer(layerId);
        });

        sourceIds.forEach((sourceId) => {
            if (!this.map?.getSource(sourceId)) {
                return;
            }

            this.map.removeSource(sourceId);
        });
    }

    private resetPhantomPolygonStyle(): void {
        if (!this.map) {
            return;
        }

        this.map.off('mousemove', this.drawCreationGeomPhantomPolygonFunctionInstance!);
        this.map.getCanvas().style.cursor = 'grab';

        if (!this.hasAccessibleMapStyle()) {
            return;
        }

        const layer = this.map.getLayer(MapCreationGeomService.MAP_CREATION_PHANTOM_LAYER_ID);
        if (layer) {
            this.map.removeLayer(MapCreationGeomService.MAP_CREATION_PHANTOM_LAYER_ID);
        }

        const source = this.map.getSource(MapCreationGeomService.MAP_CREATION_PHANTOM_SOURCE_ID);
        if (!source) {
            return;
        }

        this.map.removeSource(MapCreationGeomService.MAP_CREATION_PHANTOM_SOURCE_ID);
    }

    private async resolveMapContext(): Promise<MapCreationContext | null> {
        const selectedLayer = this.selectedLayer();
        const selectedDeposit = this.selectedDeposit();

        if (selectedLayer && selectedDeposit) {
            const entityId = v4();
            this.entityId.set(entityId);

            return {
                deposit: selectedDeposit,
                entityId,
                layer: selectedLayer,
            };
        }

        const layerIds = [...this.mapEditModeService.activeEditLayerIds()];
        if (!layerIds.length) {
            return null;
        }

        const layer = await firstValueFrom(this.layersService.layerControllerGetOne({ id: layerIds[0] }));
        if (!layer?.deposit) {
            return null;
        }

        const deposit = await firstValueFrom(
            this.depositsService.depositControllerGetDepositWithFolderAndStandard({ id: layer.deposit }),
        );

        const entityId = v4();
        this.entityId.set(entityId);
        this.createdEntityIds.push(entityId);

        return {
            deposit,
            entityId,
            layer,
        };
    }

    private setLayerStyle(): void {
        if (!this.selectedLayer()) {
            return;
        }

        const selectedSource = this.sourcesService
            .sources()
            .find((source: MapBaseSource<unknown>) => this.selectedLayer()!.id === source.sourceId);

        if (!selectedSource) {
            return;
        }

        const activeStyle = selectedSource.style.find(
            (style: MapboxStyle) => style.type === this.mapEditModeService.activeGeometryType(),
        );

        this.activeLayerStyle.set(activeStyle ?? null);
    }

    private transformLayerGeometryTypeToCreationMode(
        layerGeometryType: Layer.GeometryTypeEnum,
    ): ActiveGeometryTypeEnum {
        switch (layerGeometryType) {
            case Layer.GeometryTypeEnum.LINE_STRING:
                return ActiveGeometryTypeEnum.LINE;
            case Layer.GeometryTypeEnum.POINT:
                return ActiveGeometryTypeEnum.SYMBOL;
            default:
                return ActiveGeometryTypeEnum.FILL;
        }
    }

    private async validateGeometry(geometryToValidate: Polygon | LineString): Promise<boolean> {
        if (this.currentGeometryPosition.length <= 1) {
            return true;
        }

        try {
            const validateGeometry = await firstValueFrom(
                this.geometryService.geometryControllerValidateGeometry({
                    validateGeometryDto: { wktGeometry: wktStringify({ ...geometryToValidate }) },
                }),
            );

            if (!validateGeometry.valid) {
                this.snackBarService.openErrorSnackBar(`validateGeometry.${validateGeometry.reason ?? 'unknownError'}`);
            }

            return validateGeometry.valid;
        } catch {
            return false;
        }
    }

    private validateShape(): boolean {
        if (this.mapEditModeService.activeGeometryType() === ActiveGeometryTypeEnum.SYMBOL) {
            return true;
        }

        return this.mapEditModeService.activeGeometryType() === ActiveGeometryTypeEnum.LINE
            ? this.currentGeometryPosition.length > 1
            : this.currentGeometryPosition.length > 2;
    }
}
